import { useEffect, useState, type FormEvent } from "react";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { HeartPulse } from "lucide-react";

// 145 -- Player health & emergency record (POPIA-sensitive). Access is limited
// to school staff + linked guardians server-side; minor records are gated on
// guardian consent (143) -- a 403 surfaces as a consent notice here.

interface Form {
  medical_conditions: string;
  allergies: string;
  has_medical_aid: boolean;
  medical_aid_name: string;
  medical_aid_number: string;
  emergency_contact_name: string;
  emergency_contact_phone: string;
}

const EMPTY: Form = {
  medical_conditions: "",
  allergies: "",
  has_medical_aid: false,
  medical_aid_name: "",
  medical_aid_number: "",
  emergency_contact_name: "",
  emergency_contact_phone: "",
};

export function PlayerHealthSection({ playerId }: { playerId: string }) {
  const [form, setForm] = useState<Form>(EMPTY);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [consentBlocked, setConsentBlocked] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    api
      .getPlayerHealth(playerId)
      .then((res) => {
        setCanEdit(res.can_edit);
        if (res.health) {
          setForm({
            medical_conditions: res.health.medical_conditions ?? "",
            allergies: res.health.allergies ?? "",
            has_medical_aid: res.health.has_medical_aid,
            medical_aid_name: res.health.medical_aid_name ?? "",
            medical_aid_number: res.health.medical_aid_number ?? "",
            emergency_contact_name: res.health.emergency_contact_name ?? "",
            emergency_contact_phone: res.health.emergency_contact_phone ?? "",
          });
        }
      })
      .catch((e) => {
        if (e instanceof api.ApiError && e.status === 403) {
          if (e.body?.error === "guardian_consent_required") setConsentBlocked(true);
          else setForbidden(true);
        } else {
          setError(e instanceof api.ApiError ? e.message : "Failed to load health record");
        }
      })
      .finally(() => setLoading(false));
  }, [playerId]);

  function set<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setSaved(false);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api.upsertPlayerHealth(playerId, {
        medical_conditions: form.medical_conditions.trim() || null,
        allergies: form.allergies.trim() || null,
        has_medical_aid: form.has_medical_aid,
        medical_aid_name: form.medical_aid_name.trim() || null,
        medical_aid_number: form.medical_aid_number.trim() || null,
        emergency_contact_name: form.emergency_contact_name.trim() || null,
        emergency_contact_phone: form.emergency_contact_phone.trim() || null,
      });
      setSaved(true);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to save health record");
    } finally {
      setSaving(false);
    }
  }

  if (forbidden) return null; // caller has no access -- hide the section entirely

  return (
    <Card className="max-w-lg" data-region="player-health">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <HeartPulse className="h-4 w-4" />
          Health & Emergency
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : consentBlocked ? (
          <p className="text-sm text-muted-foreground" data-state="consent-required">
            This is a minor&apos;s health record. It is locked until a guardian records
            consent to the child-data policy.
          </p>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="player-health">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="medical-conditions">Medical conditions</Label>
              <textarea
                id="medical-conditions"
                data-field="medical-conditions"
                value={form.medical_conditions}
                onChange={(e) => set("medical_conditions", e.target.value)}
                disabled={!canEdit}
                rows={2}
                className="flex rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              />
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="allergies">Allergies</Label>
              <textarea
                id="allergies"
                data-field="allergies"
                value={form.allergies}
                onChange={(e) => set("allergies", e.target.value)}
                disabled={!canEdit}
                rows={2}
                className="flex rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
              />
            </div>

            <div className="flex items-center gap-3">
              <input
                id="has-medical-aid"
                data-field="has-medical-aid"
                type="checkbox"
                checked={form.has_medical_aid}
                onChange={(e) => set("has_medical_aid", e.target.checked)}
                disabled={!canEdit}
                className="h-4 w-4 rounded border-input"
              />
              <Label htmlFor="has-medical-aid">Has medical aid</Label>
            </div>

            {form.has_medical_aid && (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="medical-aid-name">Medical aid name</Label>
                  <Input
                    id="medical-aid-name"
                    data-field="medical-aid-name"
                    value={form.medical_aid_name}
                    onChange={(e) => set("medical_aid_name", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label htmlFor="medical-aid-number">Medical aid number</Label>
                  <Input
                    id="medical-aid-number"
                    data-field="medical-aid-number"
                    value={form.medical_aid_number}
                    onChange={(e) => set("medical_aid_number", e.target.value)}
                    disabled={!canEdit}
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-2">
                <Label htmlFor="emergency-name">Emergency contact name</Label>
                <Input
                  id="emergency-name"
                  data-field="emergency-contact-name"
                  value={form.emergency_contact_name}
                  onChange={(e) => set("emergency_contact_name", e.target.value)}
                  disabled={!canEdit}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="emergency-phone">Emergency contact phone</Label>
                <Input
                  id="emergency-phone"
                  data-field="emergency-contact-phone"
                  value={form.emergency_contact_phone}
                  onChange={(e) => set("emergency_contact_phone", e.target.value)}
                  disabled={!canEdit}
                />
              </div>
            </div>

            {canEdit && (
              <div className="flex items-center gap-3">
                <Button type="submit" data-action="save-health" disabled={saving}>
                  {saving ? "Saving…" : "Save health record"}
                </Button>
                {saved && <span className="text-sm text-muted-foreground">Saved</span>}
              </div>
            )}
          </form>
        )}
      </CardContent>
    </Card>
  );
}
